import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Drawer,
  FormControlLabel,
  IconButton,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { SafeMarkdown } from './safe-markdown';
import {
  KbConflictCheck,
  KbConflictConsequence,
  KbConflictDispositionAction,
  KbConflictFinding,
  KbConflictRelation,
  KB_CONFLICT_TERMINAL_STATES,
} from './support-agent-api';

/**
 * Findings drawer for the KB editor's "Check for conflicts" (Michael, 9/19;
 * PLANS/KB_CONFLICT_CHECK_BUILD_SPEC_2026_09_19.md in the agent workspace).
 *
 * The check is advisory: it flags where the draft may disagree with a
 * published article (or with itself) and quotes both sides verbatim; the
 * POM Master decides. Nothing here edits or publishes anything. Wording is
 * deliberate: an empty result says "no potential conflicts found in the
 * retrieved passages", never "clean" — the engine compares the draft against
 * the passages retrieval surfaced, not the whole knowledge base, and the
 * coverage line says how many.
 *
 * Findings arrive already ordered safety > operational > editorial, then
 * conflict > missing qualification > uncertain > compatible. Within a pair
 * the engine keeps a verified quote over an unverified one, but an
 * unverified SAFETY finding still outranks a verified operational one — it is
 * flagged "quote unverified" and the run is `partial`, so the reviewer is
 * pointed at it rather than shielded from it.
 */

export type KbConflictJumpTarget = { start: number; end: number };

export interface KbConflictCheckDrawerProps {
  open: boolean;
  onClose: () => void;
  check: KbConflictCheck | null;
  /** True while the initial POST or a GET is in flight. */
  loading: boolean;
  /** A request-level error (not a `failed` check — that comes from `check.error`). */
  error: string | null;
  /** Seconds since the check was requested, for the "Checking…" line. */
  elapsedSeconds?: number;
  onRunAgain?: () => void;
  /** Resume polling the same check after a timeout or read failure. */
  onRefresh?: () => void;
  /** Select the quoted span in the editor. `start`/`end` are body offsets. */
  onJumpToDraft?: (target: KbConflictJumpTarget) => void;
  /** Read-only view of the existing article; resolves its current body. */
  onLoadArticle?: (docId: string) => Promise<{ title: string; body: string }>;
  onDisposition?: (findingId: string, action: KbConflictDispositionAction, reason?: string) => Promise<void>;
  /** Finding id whose disposition request is in flight. */
  dispositionBusy?: string | null;
}

const RELATION_LABEL: Record<KbConflictRelation, string> = {
  potential_conflict: 'Potential conflict',
  missing_qualification: 'Missing qualification',
  uncertain: 'Uncertain',
  compatible: 'Compatible',
};

const RELATION_COLOR: Record<KbConflictRelation, 'error' | 'warning' | 'default' | 'success'> = {
  potential_conflict: 'error',
  missing_qualification: 'warning',
  uncertain: 'default',
  compatible: 'success',
};

const CONSEQUENCE_COLOR: Record<KbConflictConsequence, 'error' | 'warning' | 'default'> = {
  safety: 'error',
  operational: 'warning',
  editorial: 'default',
};

const ACTION_LABEL: Record<KbConflictDispositionAction, string> = {
  fix_draft: "I'll fix the draft",
  propose_edit: 'The other article needs the edit',
  dismiss: 'Dismissed',
  accept_risk: 'Risk accepted',
};

/**
 * Human wording for pom-2-api's conflict-check error codes
 * (kbConflictCheck.service.ts). Unknown responses use safe local wording.
 */
/**
 * An error whose message is already safe to show a POM Master — produced by
 * `describeConflictCheckError`, never raw backend text. FindingCard shows the
 * message of THIS type only and masks anything else, so a caller that throws
 * a plain Error cannot leak internals into the dialog.
 */
export class ConflictCheckUserError extends Error {}

const DISPOSITION_FALLBACK = 'Could not record the decision. Try again.';

function dispositionErrorMessage(err: unknown): string {
  return err instanceof ConflictCheckUserError && err.message ? err.message : DISPOSITION_FALLBACK;
}

export function describeConflictCheckError(code: unknown, fallback: string): string {
  switch (code) {
    case 'SUPPORT_AGENT_CONFLICT_CHECK_DISABLED':
      return 'Conflict checking is not enabled in this environment.';
    case 'SUPPORT_AGENT_CONFLICT_CHECK_UNAVAILABLE':
      return 'The conflict checker is not reachable from this environment right now (not deployed here yet, or down). Try again later.';
    case 'SUPPORT_AGENT_CONFLICT_CHECK_ENQUEUE_FAILED':
      return 'The check was recorded but could not be queued. Run it again.';
    case 'SUPPORT_AGENT_CONFLICT_CHECK_NOT_FOUND':
      return 'That check no longer exists. Run it again.';
    case 'SUPPORT_AGENT_CONFLICT_CHECK_NOT_FINISHED':
      return 'The check is still running. Wait for it to finish before recording decisions.';
    case 'SUPPORT_AGENT_CONFLICT_CHECK_FAILED':
      return 'This check failed, so its findings cannot take decisions. Run it again.';
    case 'SUPPORT_AGENT_CONFLICT_FINDING_NOT_FOUND':
      return 'That finding is no longer on this check. Run the check again.';
    case 'SUPPORT_AGENT_CONFLICT_CHECK_BAD_REQUEST':
      return 'The conflict checker could not accept this request. Review the draft and try again.';
    case 'KB_DOC_NOT_FOUND':
      return 'The article being edited no longer exists.';
    default:
      return fallback;
  }
}

export function describeVectorStatus(status: string | undefined): string {
  switch (status) {
    case 'ok':
      return 'vector search ok';
    case 'pending-chunks':
      return 'vector search incomplete (some articles have no vectors yet)';
    case 'failed':
      return 'vector search failed (keyword search only)';
    case 'skipped':
      return 'vector search not configured (keyword search only)';
    default:
      return '';
  }
}

export function describeJudgeStatus(judge: KbConflictCheck['judge']): string {
  if (!judge) return '';
  if (judge.status === 'skipped') return 'judge not configured — no findings could be produced';
  const parts: string[] = [];
  if (judge.unitsFailed > 0) parts.push(`${judge.unitsFailed} passage${judge.unitsFailed === 1 ? '' : 's'} not judged (model error)`);
  if (judge.unitsSkippedDeadline > 0) parts.push(`${judge.unitsSkippedDeadline} not judged (time limit)`);
  if ((judge.unitsCandidatesGone ?? 0) > 0) parts.push(`${judge.unitsCandidatesGone} not judged (articles changed mid-check)`);
  if (judge.invalidQuotes > 0) parts.push(`${judge.invalidQuotes} finding${judge.invalidQuotes === 1 ? '' : 's'} with an unverified quote`);
  if (judge.internal !== 'ok') parts.push(`draft-vs-itself check ${judge.internal === 'skipped-too-long' ? 'skipped (draft too long)' : judge.internal}`);
  return parts.join('; ');
}

/** The one line that says what was actually compared — never a promise about the whole KB. */
export function coverageLine(check: KbConflictCheck): string {
  const c = check.coverage;
  if (!c) return '';
  const total = check.corpus?.publishedDocs;
  const articles = total !== undefined ? `${c.candidateArticles} of ${total} published articles` : `${c.candidateArticles} articles`;
  return `Compared ${c.units} draft passage${c.units === 1 ? '' : 's'} against ${c.candidateChunks} retrieved passages from ${articles} · ${describeVectorStatus(c.vectorStatus)}`;
}

function FindingCard({
  finding,
  onJumpToDraft,
  onLoadArticle,
  onDisposition,
  busy,
}: {
  finding: KbConflictFinding;
  onJumpToDraft?: (target: KbConflictJumpTarget) => void;
  onLoadArticle?: (docId: string) => Promise<{ title: string; body: string }>;
  onDisposition?: (findingId: string, action: KbConflictDispositionAction, reason?: string) => Promise<void>;
  busy: boolean;
}) {
  // `reasonAction` is kept while the dialog animates closed so its title does
  // not flip mid-transition; `reasonOpen` alone decides visibility.
  const [reasonAction, setReasonAction] = useState<'dismiss' | 'accept_risk'>('dismiss');
  const [reasonOpen, setReasonOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [actionError, setActionError] = useState<string | null>(null);
  const [reasonError, setReasonError] = useState<string | null>(null);
  const [article, setArticle] = useState<{ title: string; body: string } | null>(null);
  const [articleOpen, setArticleOpen] = useState(false);
  const [articleError, setArticleError] = useState<string | null>(null);
  const [articleLoading, setArticleLoading] = useState(false);

  const existing = finding.existing;
  const canJump = !!onJumpToDraft && finding.draft.offsetStart !== null && finding.draft.offsetEnd !== null;

  const openReason = (action: 'dismiss' | 'accept_risk') => {
    setReasonAction(action);
    setReasonError(null);
    setReasonOpen(true);
  };

  const submitReason = async () => {
    if (!onDisposition) return;
    if (!reason.trim()) {
      setReasonError('A reason is required.');
      return;
    }
    setReasonError(null);
    try {
      await onDisposition(finding.id, reasonAction, reason.trim());
      setReasonOpen(false);
      setReason('');
    } catch (err) {
      setReasonError(dispositionErrorMessage(err));
    }
  };

  const recordAction = async (action: KbConflictDispositionAction) => {
    if (!onDisposition) return;
    setActionError(null);
    try {
      await onDisposition(finding.id, action);
    } catch (err) {
      setActionError(dispositionErrorMessage(err));
    }
  };

  const openArticle = async () => {
    if (!onLoadArticle || existing.kind !== 'chunk') return;
    setArticleOpen(true);
    setArticleError(null);
    setArticleLoading(true);
    // Fetch current text on every open; never present a prior body as a successful reload.
    setArticle(null);
    try {
      setArticle(await onLoadArticle(existing.docId));
    } catch {
      setArticleError('Failed to load the article. Try again.');
    } finally {
      setArticleLoading(false);
    }
  };

  return (
    <Box
      data-testid={`finding-${finding.id}`}
      sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.5, mb: 1.5, opacity: finding.disposition ? 0.75 : 1 }}
    >
      <Stack direction="row" spacing={0.5} flexWrap="wrap" alignItems="center" sx={{ mb: 1 }}>
        <Chip size="small" color={CONSEQUENCE_COLOR[finding.consequence]} label={finding.consequence} />
        <Chip size="small" variant="outlined" color={RELATION_COLOR[finding.relation]} label={RELATION_LABEL[finding.relation]} />
        {finding.kind === 'internal' && <Chip size="small" variant="outlined" label="within the draft" />}
        {!finding.quoteValid && (
          <Tooltip title="The model's quote could not be matched verbatim to the text it cites, even after one repair attempt. Treat the finding as a pointer, not evidence.">
            <Chip size="small" color="warning" variant="outlined" label="quote unverified" />
          </Tooltip>
        )}
        <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
          {finding.id}
        </Typography>
      </Stack>

      <Typography variant="body2" sx={{ mb: 0.5 }}>
        <strong>Draft:</strong> “{finding.draft.quote}”
      </Typography>
      <Typography variant="body2" sx={{ mb: 0.5 }}>
        <strong>{existing.kind === 'chunk' ? `Existing — ${existing.docTitle}${existing.heading ? ` › ${existing.heading}` : ''}` : `Draft, section “${existing.sectionHeading || 'no heading'}”`}:</strong>{' '}
        “{existing.quote}”
      </Typography>
      {(finding.conditions.draft.length > 0 || finding.conditions.existing.length > 0) && (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
          Conditions — draft: {finding.conditions.draft.join(', ') || 'none stated'}; existing: {finding.conditions.existing.join(', ') || 'none stated'}
        </Typography>
      )}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        <strong>Why flagged:</strong> {finding.why}
        {existing.kind === 'chunk' && existing.foundBy?.length > 0 && (
          <>
            {' '}
            <Typography component="span" variant="caption" color="text.secondary">
              (found by {existing.foundBy.map((r) => (r === 'bm25' ? 'keyword' : 'vector')).join(' + ')})
            </Typography>
          </>
        )}
      </Typography>

      {actionError && <Alert severity="error" sx={{ mb: 1 }}>{actionError}</Alert>}
      {finding.disposition ? (
        <Alert severity="info" sx={{ py: 0 }}>
          {ACTION_LABEL[finding.disposition.action]}
          {finding.disposition.reason ? ` — ${finding.disposition.reason}` : ''} · {finding.disposition.by},{' '}
          {new Date(finding.disposition.at).toLocaleString()}
        </Alert>
      ) : (
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {canJump && (
            <Button size="small" onClick={() => onJumpToDraft!({ start: finding.draft.offsetStart!, end: finding.draft.offsetEnd! })}>
              Edit draft
            </Button>
          )}
          {existing.kind === 'chunk' && onLoadArticle && (
            <Button size="small" onClick={openArticle}>
              View article
            </Button>
          )}
          {onDisposition && (
            <>
              <Button size="small" disabled={busy} onClick={() => recordAction('fix_draft')}>
                I&apos;ll fix the draft
              </Button>
              {existing.kind === 'chunk' && (
                <Button size="small" disabled={busy} onClick={() => recordAction('propose_edit')}>
                  Other article needs the edit
                </Button>
              )}
              <Button size="small" disabled={busy} onClick={() => openReason('dismiss')}>
                Dismiss…
              </Button>
              <Button size="small" color="warning" disabled={busy} onClick={() => openReason('accept_risk')}>
                Accept risk…
              </Button>
            </>
          )}
        </Stack>
      )}

      <Dialog open={reasonOpen} onClose={() => setReasonOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{reasonAction === 'dismiss' ? 'Dismiss this finding' : 'Accept this risk'}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {reasonAction === 'dismiss'
              ? 'Say why the two statements do not actually conflict (different device, mode, setting…). Recorded with your name.'
              : 'The disagreement is real and you are publishing anyway. Say why. Recorded with your name.'}
          </Typography>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={2}
            label="Reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            error={!!reasonError}
            helperText={reasonError ?? undefined}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReasonOpen(false)}>Cancel</Button>
          <Button variant="contained" disabled={busy} onClick={submitReason}>
            {reasonAction === 'dismiss' ? 'Dismiss' : 'Accept risk'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={articleOpen} onClose={() => setArticleOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
            <Typography variant="h6">{article?.title ?? (existing.kind === 'chunk' ? existing.docTitle : 'Article')}</Typography>
            <IconButton onClick={() => setArticleOpen(false)} size="small" aria-label="Close">
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          {articleLoading && <CircularProgress />}
          {articleError && <Alert severity="error" action={<Button color="inherit" onClick={openArticle} disabled={articleLoading}>Try again</Button>}>{articleError}</Alert>}
          {article && !articleLoading && (
            <>
              <Alert severity="info" sx={{ mb: 1 }}>
                Quoted passage: “{existing.quote}”
              </Alert>
              <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.5 }}>
                <SafeMarkdown source={article.body} />
              </Box>
            </>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
}

export const KbConflictCheckDrawer: React.FC<KbConflictCheckDrawerProps> = ({
  open,
  onClose,
  check,
  loading,
  error,
  elapsedSeconds,
  onRunAgain,
  onRefresh,
  onJumpToDraft,
  onLoadArticle,
  onDisposition,
  dispositionBusy,
}) => {
  const [showCompatible, setShowCompatible] = useState(false);
  const findings = useMemo(() => check?.findings ?? [], [check]);
  const compatible = useMemo(() => findings.filter((f) => f.relation === 'compatible'), [findings]);
  const visible = useMemo(() => (showCompatible ? findings : findings.filter((f) => f.relation !== 'compatible')), [findings, showCompatible]);
  const terminal = !!check && KB_CONFLICT_TERMINAL_STATES.has(check.state);
  const judgeNote = check ? describeJudgeStatus(check.judge) : '';

  return (
    <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{ sx: { width: 560 } }}>
      <Box sx={{ p: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="h6">Potential conflicts</Typography>
            {check && (
              <Chip
                size="small"
                label={check.state}
                color={check.state === 'completed' ? 'success' : check.state === 'partial' ? 'warning' : check.state === 'failed' ? 'error' : 'default'}
              />
            )}
          </Stack>
          <IconButton onClick={onClose} size="small" aria-label="Close">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
        <Divider sx={{ mb: 2 }} />

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {!check && loading && (
          <Stack direction="row" spacing={1} alignItems="center">
            <CircularProgress size={18} />
            <Typography variant="body2">Starting the check…</Typography>
          </Stack>
        )}

        {check && !terminal && loading && (
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
            <CircularProgress size={18} />
            <Typography variant="body2">
              Checking{elapsedSeconds !== undefined ? ` — ${elapsedSeconds}s` : ''}. The draft is split into passages, each is searched against the
              published knowledge base, and a model compares what comes back.
            </Typography>
          </Stack>
        )}

        {check && check.state === 'failed' && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {describeConflictCheckError(check.error, 'The check could not run. Try again.')}
          </Alert>
        )}

        {check && terminal && check.state !== 'failed' && (
          <>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: judgeNote ? 0.5 : 2 }}>
              {coverageLine(check)}
            </Typography>
            {check.state === 'partial' && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                Incomplete check{judgeNote ? `: ${judgeNote}` : ''}. Some evidence may be missing — treat “no findings” below as “none found in what was compared”.
              </Alert>
            )}
            {visible.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                No potential conflicts found in the retrieved passages.
                {compatible.length > 0 ? ` ${compatible.length} agreeing passage${compatible.length === 1 ? '' : 's'} hidden.` : ''}
              </Typography>
            )}
            {visible.map((f) => (
              <FindingCard
                key={`${check._id}:${f.id}`}
                finding={f}
                onJumpToDraft={onJumpToDraft}
                onLoadArticle={onLoadArticle}
                onDisposition={onDisposition}
                busy={dispositionBusy === f.id}
              />
            ))}
            {compatible.length > 0 && (
              <FormControlLabel
                control={<Switch size="small" checked={showCompatible} onChange={(e) => setShowCompatible(e.target.checked)} />}
                label={`Show ${compatible.length} agreeing passage${compatible.length === 1 ? '' : 's'}`}
              />
            )}
            {check.usage && (
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 2 }}>
                {check.usage.judgeCalls} model call{check.usage.judgeCalls === 1 ? '' : 's'}
                {check.usage.costUsd !== null ? ` · $${check.usage.costUsd.toFixed(3)}` : ''}
                {check.finishedAt && check.startedAt
                  ? ` · ${Math.round((new Date(check.finishedAt).getTime() - new Date(check.startedAt).getTime()) / 1000)}s`
                  : ''}
              </Typography>
            )}
          </>
        )}

        {onRefresh && error && !loading && !terminal && (
          <Button size="small" sx={{ mt: 2 }} onClick={onRefresh}>Check status again</Button>
        )}

        {onRunAgain && !loading && (error || (check && terminal)) && (
          <Button size="small" sx={{ mt: 2 }} onClick={onRunAgain}>
            Run again on the current draft
          </Button>
        )}
      </Box>
    </Drawer>
  );
};
